import React from 'react';
import './AlertSteps.css';
import {
  COMPARATORS,
  COMPARISON_SHAPES,
  SEVERITIES,
  VOLUME_GUARD_DEFAULT_MAX_MATCHES,
  shapeUses,
} from '../alertSpec';
import { scheduleLabel } from '../../../../utils/scheduleLabel';
import { defaultScheduleZone } from '../../shared/ScheduleZoneField';

/**
 * The review card, extended from Query / Delivery / Schedule to also carry
 * Threshold, Channels and Severity.
 *
 * **The guard row shows what is in force, not what is stored.** Those differ
 * whenever the author left the cap empty: nothing is stored and 25 applies at
 * evaluation. A review card showing "—" there would be describing the database
 * rather than the alert, and the author would be reviewing a number that is not
 * the one that will run.
 */
const AlertReviewCard = ({ formData }) => {
  const alert = formData.alert ?? {};
  const trigger = formData.trigger ?? {};

  const schedule = scheduleLabel(trigger, formData.timezone || defaultScheduleZone());

  const queryText = (formData.reportQuery || formData.queryName || '').trim();

  const deliveryParts = [];
  if (formData.reportEmailToSelf !== false) {
    deliveryParts.push(
      formData.userEmail && formData.userEmail.trim()
        ? formData.userEmail.trim()
        : 'Your account email'
    );
  }
  deliveryParts.push(...(formData.reportRecipients ?? []));
  const deliveryDisplay = deliveryParts.length > 0 ? deliveryParts.join(', ') : '—';

  const shape = COMPARISON_SHAPES.find((s) => s.id === alert.shape);
  const comparator = COMPARATORS.find((c) => c.id === alert.comparator);

  // Describes whichever shape is in force, in result columns rather than in
  // tables — the query is the row above, and it is what says where the columns
  // came from. An any-rows comparison has no comparator or threshold to state,
  // and saying "≥ 0" there would describe a rule the engine never applies.
  const thresholdDisplay = (() => {
    if (!shape || !alert.subjectColumn) return '—';
    if (shape.id === 'any_rows') return `${shape.label}, per ${alert.subjectColumn}`;

    const limit = alert.thresholdKind === 'column'
      ? `each subject's own ${alert.thresholdColumn}`
      : alert.threshold;
    const parts = [];
    if (alert.measureColumn && comparator && limit !== '' && limit != null) {
      parts.push(`${alert.measureColumn} ${comparator.label} ${limit}`);
    } else {
      return '—';
    }
    if (shapeUses(alert.shape, 'runLength') && alert.runLength) {
      const unit = alert.periodUnit ? `${alert.periodUnit}s` : 'periods';
      parts.push(`for ${alert.runLength} consecutive ${unit}`);
    }
    parts.push(`per ${alert.subjectColumn}`);
    return parts.join(', ');
  })();

  const channelsDisplay = (alert.channels ?? []).length > 0
    ? alert.channels.join(', ')
    : '—';

  const severityDisplay = SEVERITIES.find((s) => s.id === (alert.severity ?? 'warning'))?.label ?? '—';

  const guardStored = alert.volumeGuardMaxMatches;
  const guardInForce = guardStored === '' || guardStored == null
    ? `${VOLUME_GUARD_DEFAULT_MAX_MATCHES} (default)`
    : String(guardStored);
  const shareInForce = alert.volumeGuardMaxShare === '' || alert.volumeGuardMaxShare == null
    ? null
    : `${alert.volumeGuardMaxShare}% of population`;

  const rows = [
    ['Query', queryText || '—'],
    ['Threshold', thresholdDisplay],
    ['Delivery', deliveryDisplay],
    ['Channels', channelsDisplay],
    ['Severity', severityDisplay],
    // **No Window row.** The window used to be authored beside the comparison
    // and is now part of the query, so restating it here would mean reading it
    // back out of the IR and describing it in words the author did not choose —
    // and the Query row above is the thing that actually carries it.
    ['Schedule', schedule],
    ['Volume guard', shareInForce ? `${guardInForce} matches, or ${shareInForce}` : `${guardInForce} matches`],
  ];

  return (
    <div className="resui-card alert-review-card">
      {rows.map(([label, value]) => (
        <div key={label} className="alert-review-row">
          <span className="alert-review-label">{label}</span>
          <span className="alert-review-value">{value}</span>
        </div>
      ))}
    </div>
  );
};

export default AlertReviewCard;
